import { Controller, type FieldValues, type Path } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { FormFieldBinding } from '../../components/formFields';
import { chipClipboardProps } from '../../components/chipClipboard';
import { renderStrataChips, strataChipLabel } from './strataChips';
import type { StrataRouteMap } from './strataRefRoutes';
import {
    joinNumbers,
    splitNumbers,
    strataOptionNumber
} from './descriptionValidation';

/**
 * A stratigraphy relation: several Befunde selectable as chips, stored as
 * comma-joined Befundnummern (the ExcaBook wire format). Options may be
 * formatted with their location ("Bef. 7000, Fl. 1, Pl. 1") — only the
 * Befundnummer is kept; typed free text is kept as-is.
 *
 * Shared by the Planum and Profil forms, which carry the same six relations;
 * the only thing that ever differed was the label catalog.
 *
 * `links` makes the chips open what they name, like the description pickers'
 * chips do; without it they are plain chips.
 *
 * Not in `components/formFields` with the general renderers: the Befundnummer
 * grammar it parses is the Befund feature's, not a form's.
 */
export function renderStrataMultiField<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    options: readonly string[],
    links?: { routes: StrataRouteMap; onOpen: (to: string) => void }
) {
    return (
        <Controller
            name={name}
            control={binding.control}
            render={({ field, fieldState }) => {
                const selected = splitNumbers(String(field.value ?? ''));
                const selectedLower = new Set(
                    selected.map((value) => value.toLowerCase())
                );
                const remaining = options.filter(
                    (option) =>
                        !selectedLower.has(
                            strataOptionNumber(option).toLowerCase()
                        )
                );
                // A pick and a paste land alike: Befundnummern only, once.
                const commit = (next: readonly string[]) => {
                    const numbers: string[] = [];
                    for (const entry of next) {
                        const number = strataOptionNumber(String(entry));
                        if (
                            number &&
                            !numbers.some(
                                (existing) =>
                                    existing.toLowerCase() ===
                                    number.toLowerCase()
                            )
                        ) {
                            numbers.push(number);
                        }
                    }
                    field.onChange(joinNumbers(numbers));
                };
                return (
                    <Autocomplete
                        multiple
                        freeSolo
                        readOnly={binding.readOnly}
                        // Copied as the chips read ("Bef. 7000, Fl. 1, Pl. 1"),
                        // not as the bare numbers stored.
                        {...chipClipboardProps({
                            value: selected,
                            getLabel: strataChipLabel(options),
                            options,
                            fromText: (text) => text,
                            onChange: commit,
                            readOnly: binding.readOnly
                        })}
                        autoSelect
                        options={remaining as string[]}
                        value={selected}
                        // Chips read as the full description, not just the
                        // Befundnummer.
                        renderValue={renderStrataChips(
                            options,
                            links?.routes,
                            links?.onOpen
                        )}
                        onChange={(_event, next) => commit(next)}
                        onBlur={field.onBlur}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                inputRef={field.ref}
                                label={binding.intl.formatMessage({
                                    id: binding.labelId(name)
                                })}
                                error={!!fieldState.error}
                                helperText={
                                    fieldState.error
                                        ? binding.validationMessage(
                                              fieldState.error.message
                                          )
                                        : undefined
                                }
                            />
                        )}
                    />
                );
            }}
        />
    );
}
